// src/pages/Extensao.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  InputBase,
  Typography,
  Fade,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { styled } from '@mui/system';
import axios from 'axios';

// Imagens por categoria (placeholders/custom)
import imgDesenvolvimento from '../../assets/cursos/desenvolvimento.png';
import imgPodologia from '../../assets/cursos/podologia.png';
import imgGerenciais from '../../assets/cursos/gerenciais.png';
import imgGastronomia from '../../assets/cursos/gastronomia.png';
import imgHospitalar from '../../assets/cursos/hospitalar.png';
import imgPortuaria from '../../assets/cursos/portuaria.png';
import imgPlaceholder from '../../assets/cursos/placeholder.png';

import { Helmet } from "react-helmet";

<Helmet>
  <title>Cursos de Extensão • Faculdade Senac Amazonas</title>
  <meta
    name="description"
    content="Cursos de Extensão da Faculdade Senac AM: capacitações práticas, rápidas e com certificação reconhecida."
  />
  <link rel="canonical" href="https://faculdade.am.senac.br/extensao" />
</Helmet>


// ===== Tipos =====
type Turma = {
  id: number | string;
  nomeTurma: string;
  formaDeExecucao?: string;
  nomeUnidadeOperativa?: string;
  enrollmentOpen?: boolean;
  linkInscricao?: string;
  linkInteresse?: string;
  sigla?: string;
  dataPeriodoExecucaoInicial?: string;              // "2025-11-25T00:00:00"
  dataPeriodoExecucaoFinal?: string;                // "2025-11-26T00:00:00"
  dataPeriodoExecucaoInicialFormatada?: string;     // "25/11/2025"
  dataPeriodoExecucaoFinalFormatada?: string;       // "26/11/2025"
  informacoesDoPlanoDoCurso?: {
    tipoDeAcao?: string;
    tipoDeAcaoId?: number;
    modalidade?: string;
    modalidadeId?: number;
    nome?: string;
    eixoTecnologico?: string;
    eixoTecnologicoId?: number;
    categoria?: string;
    categoriaId?: number;
    segmento?: string;
    segmentoId?: number;
  };
};

// ===== Utils =====
const getImagemCurso = (nome: string): string => {
  const n = nome.toLowerCase();
  if (
    n.includes('inteligência artificial') ||
    n.includes('inteligencia artificial') ||
    n.includes('ia')
  )
    return imgDesenvolvimento;
  if (n.includes('podologia')) return imgPodologia;
  if (n.includes('desenvolvimento')) return imgDesenvolvimento;
  if (n.includes('portuária') || n.includes('portuaria')) return imgPortuaria;
  if (n.includes('gerenciais')) return imgGerenciais;
  if (n.includes('gastronomia')) return imgGastronomia;
  if (n.includes('hospitalar')) return imgHospitalar;
  return imgPlaceholder;
};

const removerAcentos = (texto: string) =>
  (texto || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const normaliza = (texto: string) => removerAcentos((texto || '').toLowerCase().trim());

// ===== Alvo específico =====
// Mantemos a unidade alvo (como no código original):
const UNIDADE_ALVO = 'faculdade de tecnologia senac';

// Novo critério de filtro: modalidade + tipo de ação
const MODALIDADE_ALVO = 'educacao superior';
const TIPO_ACAO_ALVO = 'extensao';
const TIPO_ACAO_ID_ALVO = 16;

// Agora o filtro NÃO usa mais sigla. Ele verifica:
// - Unidade = Faculdade de Tecnologia Senac
// - Modalidade = Educação Superior
// - Tipo de ação = Extensão (por texto ou por id 16)
const matchesTargetCourse = (t: Turma): boolean => {
  const unidadeNorm = normaliza(t?.nomeUnidadeOperativa || '');
  const info = t?.informacoesDoPlanoDoCurso || {};
  const modalidadeNorm = normaliza(info?.modalidade || '');
  const tipoAcaoNorm = normaliza(info?.tipoDeAcao || '');
  const tipoAcaoId = info?.tipoDeAcaoId;

  const unidadeConfere = unidadeNorm === UNIDADE_ALVO;
  const modalidadeConfere = modalidadeNorm === MODALIDADE_ALVO;
  const tipoAcaoConfere =
    tipoAcaoNorm === TIPO_ACAO_ALVO || tipoAcaoId === TIPO_ACAO_ID_ALVO;

  return unidadeConfere && modalidadeConfere && tipoAcaoConfere;
};

// ===== Estilos =====
const SearchInput = styled(InputBase)({
  padding: '14px 18px',
  width: '100%',
  maxWidth: 480,
  border: '1px solid #ccc',
  borderRadius: 10,
  fontSize: '1rem',
});

const CardCurso = styled(Box)<{ imagem: string }>(({ imagem }) => ({
  position: 'relative',
  borderRadius: 12,
  overflow: 'hidden',
  backgroundImage: `url(${imagem})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  minHeight: 400,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: 24,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  transition: 'transform 0.3s ease',
  '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' },
  color: '#fff',
}));

const Overlay = styled('div')({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.9))',
  zIndex: 1,
});

const CardContent = styled(Box)({ position: 'relative', zIndex: 2 });

// ===== Componente =====
const Extensao: React.FC = () => {
  const [turmas, setTurmas] = useState<Turma[]>([]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [atualizadoEm, setAtualizadoEm] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCursos = async () => {
      setLoading(true);

      const isProd = process.env.NODE_ENV === 'production';

      const bases = isProd
        ? ['/api/turmas'] // produção: apenas same-origin via Apache
        : ['/turmas', '/api/turmas', 'http://localhost:5000/turmas', 'http://localhost:5000/api/turmas'];

      let payload: any = null;
      let lastErr: any = null;

      for (const url of bases) {
        try {
          const resp = await axios.get(url, { timeout: 20000 });
          payload = resp.data;
          console.log('✅ Turmas carregadas de:', url);
          break;
        } catch (err) {
          lastErr = err;
          console.warn('Falha ao buscar em', url, err);
        }
      }

      if (!payload) {
        console.error('Erro ao carregar cursos (todas URLs falharam):', lastErr);
        setLoading(false);
        return;
      }

      setAtualizadoEm(payload.atualizadoEm || null);

      // Filtra somente turmas da unidade alvo + (modalidade = Educação Superior) + (tipo de ação = Extensão)
      const turmasFiltradas: Turma[] = [
        ...(payload.turmasAtivas || []),
        ...(payload.turmasEmBreve || []),
      ].filter(matchesTargetCourse);

      setTurmas(turmasFiltradas);
      setTimeout(() => setLoading(false), 800);
    };

    fetchCursos();
  }, []);

  const termosBusca = normaliza(busca).split(' ').filter(Boolean);

  const cursosFiltrados = turmas.filter((turma) => {
    if (termosBusca.length === 0) return true;
    const nomeBase =
      turma.informacoesDoPlanoDoCurso?.nome && turma.informacoesDoPlanoDoCurso?.nome.trim() !== ''
        ? turma.informacoesDoPlanoDoCurso.nome
        : turma.nomeTurma;

    const nomeNorm = normaliza(nomeBase);

    return termosBusca.every((termo) => nomeNorm.includes(termo));
  });

  return (
    <Container sx={{ py: 8 }}>
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Typography variant="h4" align="center" gutterBottom fontWeight={600}>
        Cursos de Extensão
      </Typography>

      {atualizadoEm && (
        <Typography variant="body2" align="center" sx={{ mb: 1, color: 'text.secondary' }}>
         
        </Typography>
      )}

      <Box textAlign="center" mb={6}>
        <SearchInput
          placeholder="Buscar por palavras do nome..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </Box>

      {!loading && (
        <>
          {cursosFiltrados.length === 0 ? (
            <Typography align="center">Nenhum curso encontrado.</Typography>
          ) : (
            <Fade in timeout={600}>
              <Grid container spacing={4}>
                {cursosFiltrados.map((turma, index) => {
                  const nomeBase =
                    turma.informacoesDoPlanoDoCurso?.nome && turma.informacoesDoPlanoDoCurso?.nome.trim() !== ''
                      ? turma.informacoesDoPlanoDoCurso.nome
                      : turma.nomeTurma;

                  const temAberta = turma.enrollmentOpen;
                  const imagem = getImagemCurso(nomeBase);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={turma.id || index}>
                      <CardCurso imagem={imagem}>
                        <Overlay />
                        <CardContent>
                          <Typography variant="h6">{nomeBase}</Typography>

                          {turma.formaDeExecucao && (
                            <Typography sx={{ mt: 0.5 }}>{turma.formaDeExecucao}</Typography>
                          )}
            {/* Informação útil do novo filtro: Data de início da turma */}
            {turma.dataPeriodoExecucaoInicialFormatada && (
              <Typography
                sx={{
                mt: 0.5,
                fontSize: '0.85rem',
                opacity: 0.9,
                }}
              >
                Início das aulas: {turma.dataPeriodoExecucaoInicialFormatada}
              </Typography>
            )}
                          {turma.nomeUnidadeOperativa && (
                            <Typography sx={{ mt: 0.5, fontSize: '0.9rem' }}>
                              Unidade: {turma.nomeUnidadeOperativa}
                            </Typography>
                          )}
                          
                          {/* Informação útil do novo filtro (opcional exibição): */}
                          {/* {turma.dataPeriodoExecucaoInicial && (
                            <Typography sx={{ mt: 0.5, fontSize: '0.85rem', opacity: 0.9 }}>
                              Modalidade: {turma.informacoesDoPlanoDoCurso.modalidade}
                            </Typography>
                          )}
                          {turma.informacoesDoPlanoDoCurso?.tipoDeAcao && (
                            <Typography sx={{ mt: 0.2, fontSize: '0.85rem', opacity: 0.9 }}>
                              Tipo de Ação: {turma.informacoesDoPlanoDoCurso.tipoDeAcao}
                            </Typography>
                          )} */}

                          <Typography sx={{ mt: 1, fontWeight: 600 }}>
                            {temAberta ? 'Matrículas abertas' : 'Registre seu interesse'}
                          </Typography>

                          <Button
                            variant="contained"
                            sx={{ mt: 2, background: 'linear-gradient(45deg, #6b46c1, #9f7aea)' }}
                            disabled={!turma.id}
                            onClick={() =>
                              navigate(`/curso-extensao/${turma.id}`, {
                                state: { variacoes: [turma] },
                              })
                            }
                          >
                            Mais Informações
                          </Button>
                        </CardContent>
                      </CardCurso>
                    </Grid>
                  );
                })}
              </Grid>
            </Fade>
          )}
        </>
      )}
    </Container>
  );
};

export default Extensao;
